import { defineStore } from "pinia";
import { UsersApi, type UserCompanyModel } from "@simbelapp/order-sdk";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { IRadioListFilterPage } from "~/utils/interfaces/radio-list-filter-interfaces";

// One selectable manager row: `id` = the user_id the backend filters on, `displayValue` = the label.
interface IManagerItem {
  id: string;
  displayValue: string;
}

function toItem(manager: UserCompanyModel): IManagerItem {
  return {
    id: manager.userId,
    displayValue: manager.displayValue,
  };
}

export const useManagersStore = defineStore("ManagersStore", {
  actions: {
    /**
     * Lazily fetch one page of selectable direct managers (offset-based, searchable) from
     * `GET /users/managers` via the typed SDK (getCompanyManagers). The list is the full
     * company-managers list (no scope param); consumed by RadioListFilter for manager_direct.
     */
    async fetchManagers(
      params: { search?: string; offset?: number; limit?: number } = {},
    ): Promise<IRadioListFilterPage> {
      const feedback = useFeedback();
      try {
        const { search = "", offset = 0, limit = 20 } = params;
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        const res = await apiInstance.getCompanyManagers({ lazyLoad: true, search, offset, limit });
        const items = (res.data ?? []).map(toItem);
        const totalCount = res.meta?.totalCount;
        const hasMore = typeof totalCount === "number" ? offset + items.length < totalCount : items.length === limit;
        return { items, hasMore };
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return { items: [], hasMore: false };
      }
    },

    /**
     * Re-resolve a persisted selection (ids -> { id, displayValue }) via the endpoint's
     * `user_ids` short-circuit — used on reload, where settings only persist the ids.
     */
    async resolveManagers(ids: string[]): Promise<IManagerItem[]> {
      if (!ids?.length) return [];
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        const res = await apiInstance.getCompanyManagers({ userIds: ids });
        return (res.data ?? []).map(toItem);
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return [];
      }
    },
  },
});
